import { BolsoLayout } from "@/components/BolsoLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuthContext } from "@/contexts/AuthContext";
import { useI18n, type Language, type Currency } from "@/contexts/I18nContext";
import { trpc } from "@/lib/trpc";
import { Coins, Globe, CheckCircle2, Pencil, Plus, Tags, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

type CategoryKind = "expense" | "income";

export default function Preferences() {
  const { user } = useAuthContext();
  const { t, language, currency, setLanguage, setCurrency } = useI18n();
  const [saved, setSaved] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [categoryKind, setCategoryKind] = useState<CategoryKind>("expense");
  const [categoryParentId, setCategoryParentId] = useState<string>("none");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingKind, setEditingKind] = useState<CategoryKind>("expense");
  const [editingParentId, setEditingParentId] = useState<string>("none");

  const utils = trpc.useUtils();
  const { data: categories = [], isLoading: categoriesLoading } = trpc.categories.list.useQuery({});
  const customCategories = categories.filter((category) => category.userId === user?.id && category.kind !== null);

  const updatePrefs = trpc.users.updatePreferences.useMutation({
    onSuccess: (_data, variables) => {
      utils.auth.me.setData(undefined, (currentUser) => currentUser ? {
        ...currentUser,
        ...(variables.language ? { language: variables.language } : {}),
        ...(variables.currency ? { currency: variables.currency } : {}),
      } : currentUser);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(t.settings.saved);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const createCategory = trpc.categories.create.useMutation({
    onSuccess: () => {
      setCategoryName("");
      setCategoryParentId("none");
      void utils.categories.list.invalidate();
      toast.success(t.settings.categories.created);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const updateCategory = trpc.categories.update.useMutation({
    onSuccess: () => {
      setEditingId(null);
      void utils.categories.list.invalidate();
      toast.success(t.settings.categories.updated);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const deleteCategory = trpc.categories.delete.useMutation({
    onSuccess: () => {
      void utils.categories.list.invalidate();
      toast.success(t.settings.categories.deleted);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const handleSavePreferences = () => {
    updatePrefs.mutate({ language, currency });
  };

  const preferenceButtonClass = (selected: boolean) => `min-h-11 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${selected ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border bg-background text-foreground"}`;
  const applyLanguage = (nextLanguage: Language) => {
    setLanguage(nextLanguage);
    updatePrefs.mutate({ language: nextLanguage });
  };
  const applyCurrency = (nextCurrency: Currency) => {
    setCurrency(nextCurrency);
    updatePrefs.mutate({ currency: nextCurrency });
  };

  const handleCreateCategory = () => {
    const name = categoryName.trim();
    if (!name) return;
    createCategory.mutate({ name, kind: categoryKind, parentId: categoryParentId === "none" ? null : Number(categoryParentId) });
  };

  const startEditing = (category: (typeof customCategories)[number]) => {
    setEditingId(category.id);
    setEditingName(category.name);
    setEditingKind(category.kind === "income" ? "income" : "expense");
    setEditingParentId(category.parentId ? String(category.parentId) : "none");
  };

  const handleUpdateCategory = () => {
    const name = editingName.trim();
    if (!editingId || !name) return;
    updateCategory.mutate({ id: editingId, name, kind: editingKind, parentId: editingParentId === "none" ? null : Number(editingParentId) });
  };

  const handleDeleteCategory = (id: number) => {
    if (window.confirm(t.settings.categories.deleteConfirm)) {
      deleteCategory.mutate({ id });
    }
  };

  const currencies: { value: Currency; label: string }[] = [
    { value: "BRL", label: t.settings.currencies.BRL },
    { value: "EUR", label: t.settings.currencies.EUR },
    { value: "USD", label: t.settings.currencies.USD },
    { value: "GBP", label: t.settings.currencies.GBP },
  ];

  const languages: { value: Language; label: string }[] = [
    { value: "pt", label: t.settings.languages.pt },
    { value: "en", label: t.settings.languages.en },
    { value: "es", label: t.settings.languages.es },
    { value: "it", label: t.settings.languages.it },
  ];

  const renderCategoryList = (kind: CategoryKind) => {
    const list = customCategories.filter((category) => category.kind === kind);
    const parentOptions = customCategories.filter((category) => category.kind === kind && category.parentId === null);
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-foreground">{kind === "expense" ? t.settings.categories.expense : t.settings.categories.income}</h3>
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.settings.categories.empty}</p>
        ) : (
          <div className="space-y-2">
            {list.map((category) => (
              <div key={category.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                {editingId === category.id ? (
                  <>
                    <Input
                      value={editingName}
                      onChange={(event) => setEditingName(event.target.value)}
                      aria-label={t.settings.categories.namePlaceholder}
                      className="h-9 flex-1"
                      autoFocus
                    />
                    <Select value={editingKind} onValueChange={(value) => setEditingKind(value as CategoryKind)}>
                      <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="expense">{t.settings.categories.expense}</SelectItem>
                        <SelectItem value="income">{t.settings.categories.income}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={editingParentId} onValueChange={setEditingParentId}>
                      <SelectTrigger className="h-9 w-40"><SelectValue placeholder={t.settings.categories.parent} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t.settings.categories.noParent}</SelectItem>
                        {parentOptions.filter((parent) => parent.id !== category.id).map((parent) => <SelectItem key={parent.id} value={String(parent.id)}>{parent.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={handleUpdateCategory} disabled={updateCategory.isPending}>{t.settings.categories.save}</Button>
                    <Button size="icon-sm" variant="ghost" onClick={() => setEditingId(null)} aria-label={t.settings.categories.cancel}><X className="h-4 w-4" /></Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm text-foreground">{category.parentId ? `↳ ${customCategories.find((parent) => parent.id === category.parentId)?.name ?? ""} / ` : ""}{category.name}</span>
                    <Button size="icon-sm" variant="ghost" onClick={() => startEditing(category)} aria-label={`${t.settings.categories.edit}: ${category.name}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon-sm" variant="ghost" onClick={() => handleDeleteCategory(category.id)} aria-label={`${t.settings.categories.delete}: ${category.name}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <BolsoLayout onNewTransaction={() => undefined}>
      <div className="p-4 lg:p-6 max-w-2xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-serif font-bold text-foreground">{t.settings.title}</h1>
        </div>

        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2"><Globe className="w-4 h-4 text-primary" />{t.settings.language}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t.settings.language}</Label>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t.settings.language}>
                {languages.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    role="radio"
                    aria-checked={language === item.value}
                    className={preferenceButtonClass(language === item.value)}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      applyLanguage(item.value);
                    }}
                    onClick={(event) => { if (event.detail === 0) applyLanguage(item.value); }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2"><Coins className="w-4 h-4 text-primary" />{t.settings.currency}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t.settings.currency}</Label>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t.settings.currency}>
                {currencies.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    role="radio"
                    aria-checked={currency === item.value}
                    className={preferenceButtonClass(currency === item.value)}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      applyCurrency(item.value);
                    }}
                    onClick={(event) => { if (event.detail === 0) applyCurrency(item.value); }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2"><Tags className="w-4 h-4 text-primary" />{t.settings.categories.title}</CardTitle>
            <p className="text-sm text-muted-foreground">{t.settings.categories.description}</p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input value={categoryName} onChange={(event) => setCategoryName(event.target.value)} placeholder={t.settings.categories.namePlaceholder} aria-label={t.settings.categories.namePlaceholder} onKeyDown={(event) => { if (event.key === "Enter") handleCreateCategory(); }} />
              <Select value={categoryKind} onValueChange={(value) => setCategoryKind(value as CategoryKind)}>
                <SelectTrigger className="sm:w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="expense">{t.settings.categories.expense}</SelectItem>
                  <SelectItem value="income">{t.settings.categories.income}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryParentId} onValueChange={setCategoryParentId}>
                <SelectTrigger className="sm:w-44"><SelectValue placeholder={t.settings.categories.parent} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t.settings.categories.noParent}</SelectItem>
                  {customCategories.filter((category) => category.kind === categoryKind && category.parentId === null).map((parent) => <SelectItem key={parent.id} value={String(parent.id)}>{parent.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={handleCreateCategory} disabled={createCategory.isPending || !categoryName.trim()} className="gap-2 whitespace-nowrap"><Plus className="h-4 w-4" />{t.settings.categories.add}</Button>
            </div>
            {categoriesLoading ? <p className="text-sm text-muted-foreground">{t.common.loading}</p> : <div className="grid gap-5 sm:grid-cols-2">{renderCategoryList("expense")}{renderCategoryList("income")}</div>}
          </CardContent>
        </Card>

        <Button onClick={handleSavePreferences} disabled={updatePrefs.isPending || saved} className="w-full gap-2">
          {saved ? <><CheckCircle2 className="w-4 h-4" /> {t.settings.saved}</> : t.settings.save}
        </Button>
      </div>
    </BolsoLayout>
  );
}
